import {
  RawSpecList,
  RawSpecListVersion,
  RawSpecValue,
  SpecValue,
  SpecList,
  SpecListVersion,
  RawSpecListVersionMinimal,
  SpecListMinimal,
  RawSpecListMinimal,
  SpecListVersionMinimal,
} from "@/types/specList";
import { mapRawToMask } from "./mask";
import { RawSpec, Spec } from "@/types/specList";

export const mapRawSpecToSpec = (raw: RawSpec): Spec => ({
  id: raw.id ?? "",
  deviceClass: raw.device_class,
  name: raw.name,
  reportName: raw.report_name,
  condition: raw.condition,
  legacyNames: raw.legacy_names ?? raw.report_name,
  dutNames: raw.dut_names,
  recipe: raw.recipe ? { id: raw.recipe.id, name: raw.recipe.name } : null,
});

export const mapRawSpecValueToSpecValue = (
  raw: RawSpecValue,
  isMasterValue: boolean,
): SpecValue => {
  return {
    id: raw.id ?? "",
    specId: raw.spec_id ?? "",
    spec: mapRawSpecToSpec(raw.spec),
    specListVersionId: raw.spec_version_id ?? "",
    lsl: raw.lsl,
    tgt: raw.tgt,
    usl: raw.usl,
    jmpYmin: raw.jmp_ymin,
    jmpYmax: raw.jmp_ymax,
    jmpYinc: raw.jmp_yinc,
    libMedian: raw.lib_median,
    libMean: raw.lib_mean,
    libStd: raw.lib_std,
    libQ90: raw.lib_q90,
    libQ10: raw.lib_q10,
    libN: raw.lib_n,
    isMasterValue,
  };
};

export const mapRawSpecListVersionToSpecListVersion = (
  raw: RawSpecListVersion,
  isMasterVersion = false,
): SpecListVersion => ({
  createdAt: new Date(raw.created_at),
  createdBy: raw.created_by,
  id: raw.id,
  specList: {
    id: raw.spec_list.id,
    name: raw.spec_list.name,
    isArchived: raw.spec_list.is_archived,
  },
  specValues: raw.spec_values.map((v) => mapRawSpecValueToSpecValue(v, isMasterVersion)),
  version: raw.version,
  uploaded: raw.uploaded,
});

export const mapRawSpecListVersionMinimalToSpecListVersionMinimal = (
  raw: RawSpecListVersionMinimal,
): SpecListVersionMinimal => ({
  createdAt: new Date(raw.created_at),
  createdBy: raw.created_by,
  id: raw.id,
  specList: {
    id: raw.spec_list.id,
    name: raw.spec_list.name,
  },
  version: raw.version,
  uploaded: raw.uploaded,
});

export const mapRawToSpecList = (raw: RawSpecList): SpecList => {
  let versions: SpecListVersion[] = [];

  if (raw.master_version) {
    versions = raw.versions.map((v) => mapRawSpecListVersionToSpecListVersion(v, false));
  } else {
    // If the current SpecList is a master spec, we want to tag the first SpecListVersion as the "master version"
    const [firstVersion, ...rest] = raw.versions;
    if (firstVersion) {
      const mappedFirstVersion = mapRawSpecListVersionToSpecListVersion(firstVersion, true);
      versions = [
        mappedFirstVersion,
        ...rest.map((v) => mapRawSpecListVersionToSpecListVersion(v, false)),
      ];
    }
  }

  const latestVersion = versions[versions.length - 1];

  return {
    id: raw.id,
    isArchived: raw.is_archived,
    latestVersion,
    mask: raw.mask ? mapRawToMask(raw.mask) : null,
    masterVersionId: raw.master_version_id,
    masterVersion: raw.master_version
      ? mapRawSpecListVersionToSpecListVersion(raw.master_version, true)
      : undefined,
    name: raw.name,
    versions,
    type: raw.type,
  };
};

export const mapRawToSpecListMinimal = (raw: RawSpecListMinimal): SpecListMinimal => {
  let versions: SpecListVersionMinimal[] = [];

  if (raw.master_version) {
    versions = raw.versions.map((v) => mapRawSpecListVersionMinimalToSpecListVersionMinimal(v));
  } else {
    // If the current SpecList is a master spec, we want to tag the first SpecListVersion as the "master version"
    const [firstVersion, ...rest] = raw.versions;
    if (firstVersion) {
      const mappedFirstVersion = mapRawSpecListVersionMinimalToSpecListVersionMinimal(firstVersion);
      versions = [
        mappedFirstVersion,
        ...rest.map((v) => mapRawSpecListVersionMinimalToSpecListVersionMinimal(v)),
      ];
    }
  }

  const latestVersion = versions[versions.length - 1];

  return {
    id: raw.id,
    isArchived: raw.is_archived,
    latestVersion,
    mask: raw.mask ? mapRawToMask(raw.mask) : null,
    masterVersion: raw.master_version
      ? mapRawSpecListVersionMinimalToSpecListVersionMinimal(raw.master_version)
      : undefined,
    name: raw.name,
    versions,
    type: raw.type,
  };
};
